import { type FormEvent, useRef, useState } from 'react';
import { BackgroundVideo } from '../components/BackgroundVideo';
import { AlertIcon, CheckIcon } from '../components/Icons';

const accessKey = import.meta.env.VITE_WEB3FORMS_ACCESS_KEY?.trim();

export function Contact() {
  const [status, setStatus] = useState<
    'idle' | 'sending' | 'sent' | 'error' | 'timeout' | 'unconfigured'
  >('idle');
  const inFlight = useRef(false);
  const [emailDraft, setEmailDraft] = useState('mailto:yaron.fanger@ylabs.ch');
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (inFlight.current || status === 'sent') return;
    const form = event.currentTarget;
    if (!form.reportValidity()) return;
    const data = new FormData(form);
    if (data.get('website') || data.get('botcheck')) {
      setStatus('error');
      return;
    }
    const values = {
      name: String(data.get('name') ?? '').trim(),
      email: String(data.get('email') ?? '').trim(),
      message: String(data.get('message') ?? '').trim(),
    };
    if (!accessKey) {
      const body = `${values.message}\n\n${values.name}\n${values.email}`;
      setEmailDraft(
        `mailto:yaron.fanger@ylabs.ch?subject=${encodeURIComponent('Arcnova 2026 website enquiry')}&body=${encodeURIComponent(body)}`,
      );
      setStatus('unconfigured');
      return;
    }
    inFlight.current = true;
    setStatus('sending');
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          ...values,
          access_key: accessKey,
          subject: 'Arcnova 2026 website enquiry',
          from_name: 'Arcnova 2026',
          replyto: values.email,
          botcheck: false,
          consent: true,
        }),
        signal: AbortSignal.timeout(15000),
      });
      const result: unknown = await response.json();
      if (
        !response.ok ||
        typeof result !== 'object' ||
        result === null ||
        !('success' in result) ||
        result.success !== true
      ) {
        throw new Error('Message not accepted');
      }
      setStatus('sent');
      form.reset();
    } catch (error) {
      setStatus(
        error instanceof DOMException && error.name === 'TimeoutError' ? 'timeout' : 'error',
      );
    } finally {
      inFlight.current = false;
    }
  }

  return (
    <section className="section_contact">
      <div className="content_contact-top">
        <section id="contact" className="contact_container">
          <div
            id="w-node-d1b94225-339e-b334-c6e3-43cee0b810cb-d6538631"
            className="contact_form-text"
          >
            <h2 className="contact_heading split-heading">
              <span
                data-word-reveal="true"
                className="heading-large heading-inline split-heading_text"
              >
                Discuss
              </span>{' '}
              <span className="contacts_heading-image">
                <img
                  src="/assets/images/Email-Icon.svg"
                  loading="lazy"
                  data-motion-id="2fe184b9-ecae-d3a9-0bf6-d005c47c5a65"
                  alt=""
                  className="icon-email"
                />
              </span>{' '}
              <span
                data-word-reveal="true"
                className="heading-large heading-inline split-heading_text"
              >
                the research
              </span>
            </h2>
            <p data-word-reveal="true" className="text-size-small">
              Contact Ylabs LLC with questions about Arcnova’s studies or to discuss related
              research and collaboration.
            </p>
          </div>
          <div
            id="w-node-_4d978aa4-f709-a2f6-e05f-7c044b291faf-d6538631"
            className="contact_form-wrapper"
          >
            <div className="form-block w-form">
              <form
                className="form"
                action="https://api.web3forms.com/submit"
                method="post"
                onSubmit={submit}
                hidden={status === 'sent'}
                aria-busy={status === 'sending'}
              >
                <input type="hidden" name="access_key" value={accessKey ?? ''} />
                <input type="hidden" name="subject" value="Arcnova 2026 website enquiry" />
                <input type="hidden" name="from_name" value="Arcnova 2026" />
                <input type="checkbox" name="botcheck" hidden tabIndex={-1} autoComplete="off" />
                <label className="sr-only" htmlFor="contact-name">
                  Name
                </label>
                <input
                  className="form-input w-input"
                  maxLength={256}
                  name="name"
                  placeholder="name"
                  type="text"
                  id="contact-name"
                  autoComplete="name"
                  required
                />
                <label className="sr-only" htmlFor="contact-email">
                  Email
                </label>
                <input
                  className="form-input w-input"
                  maxLength={256}
                  name="email"
                  placeholder="your@mail.com"
                  type="email"
                  id="contact-email"
                  autoComplete="email"
                  required
                />
                <label className="sr-only" htmlFor="contact-message">
                  Message
                </label>
                <textarea
                  required
                  placeholder="message"
                  maxLength={5000}
                  id="contact-message"
                  name="message"
                  className="form-input text-area w-input"
                />
                <div className="form-honeypot" aria-hidden="true">
                  <label htmlFor="contact-website">Website</label>
                  <input id="contact-website" name="website" tabIndex={-1} autoComplete="off" />
                </div>
                <label className="w-checkbox form-checkbox">
                  <input type="checkbox" name="consent" required className="form-checkbox-icon" />
                  <span className="form-checkbox-label w-form-label">
                    By submitting this form, you agree to our{' '}
                    <a href="/privacy-policy" target="_blank" rel="noreferrer">
                      Privacy Policy
                    </a>
                  </span>
                </label>
                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="button margin-top w-button"
                >
                  {status === 'sending' ? 'Please wait…' : 'send message'}
                </button>
              </form>
              <noscript>
                <p>
                  You can also email{' '}
                  <a href="mailto:yaron.fanger@ylabs.ch">yaron.fanger@ylabs.ch</a> directly.
                </p>
              </noscript>
              <div aria-live="polite">
                {status === 'sent' && (
                  <div className="success-message">
                    <div className="div-block-13">
                      <div className="icon-small">
                        <CheckIcon />
                      </div>
                      <div className="success_text">
                        Thank you! Your enquiry has been submitted.
                      </div>
                    </div>
                  </div>
                )}
                {(status === 'error' || status === 'timeout') && (
                  <div className="error-message">
                    <div className="div-block-14">
                      <div className="icon-embed-xsmall">
                        <AlertIcon />
                      </div>
                      <div>
                        {status === 'timeout'
                          ? 'The request timed out, so we couldn’t confirm delivery. Your message is still here. Please try again later or email '
                          : 'We couldn’t send your message. Please try again or email '}
                        <a href="mailto:yaron.fanger@ylabs.ch">yaron.fanger@ylabs.ch</a>.
                      </div>
                    </div>
                  </div>
                )}
                {status === 'unconfigured' && (
                  <div className="error-message">
                    Your message has not been sent. Please{' '}
                    <a href={emailDraft}>open it in your email app</a> to contact{' '}
                    yaron.fanger@ylabs.ch.
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
        <BackgroundVideo
          motionId="c6adcadd-85fc-e2c8-8847-1478b7d486f9"
          name="13504946-uhd_3840_2160_30fps-1"
          className="contact_video"
          tone="green"
        >
          <div
            data-motion-id="9580cc5d-5030-208e-c3e1-0c8b7db73aab"
            className="image_shading is-green"
          />
        </BackgroundVideo>
      </div>
      <div className="stripe-wrapper">
        <img
          src="/assets/images/Stripe.svg"
          loading="lazy"
          alt="Arcnova logo text"
          className="stripe"
        />
        <img src="/assets/images/Stripe.svg" loading="lazy" alt="" className="stripe" />
      </div>
    </section>
  );
}
